import { Fragment, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useMenu } from "@refinedev/core";
import { useThemedLayoutContext } from "@refinedev/mui";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { AdminMenuGroup } from "../../utils/adminAccess";

// Whether Admin was left open, in this browser only: the seller and the admin
// pages use different layouts, so the menu is drawn anew between them.
const OPEN_KEY = "admin.menuOpen";

const readOpen = (): boolean | null => {
  try {
    const stored = window.localStorage.getItem(OPEN_KEY);
    return stored === null ? null : stored === "1";
  } catch {
    return null;
  }
};

const writeOpen = (open: boolean) => {
  try {
    window.localStorage.setItem(OPEN_KEY, open ? "1" : "0");
  } catch {
    // storage unavailable: the menu just isn't remembered
  }
};

// The "Admin" entry of the sidebar, drawn here instead of by ThemedSiderV2 so
// its pages can sit under small, always open section titles (the groups of
// ADMIN_PAGES) rather than behind extra collapsible levels. Items look and
// behave like ThemedSiderV2's own (same buttons, the theme styles them).
export const AdminMenu = () => {
  const { translate } = useTranslation("pages.admin");
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const { siderCollapsed, setSiderCollapsed, setMobileSiderOpen } =
    useThemedLayoutContext();
  const admin = menuItems.find((item) => item.key === "/admin");
  const onAdminPage = defaultOpenKeys.includes("/admin");
  // the last choice, or open on an admin page when there is none
  const [open, setOpen] = useState(() => readOpen() ?? onAdminPage);

  // opening an admin page from elsewhere (a link, the address bar) opens it
  useEffect(() => {
    if (onAdminPage) setOpen(true);
  }, [onAdminPage]);

  if (!admin) return null;

  const label = admin.label ?? admin.name;
  const choose = (next: boolean) => {
    setOpen(next);
    writeOpen(next);
  };
  const toggle = () => {
    if (siderCollapsed) {
      setSiderCollapsed(false);
      choose(true);
      return;
    }
    choose(!open);
  };

  return (
    <div>
      <Tooltip
        title={label}
        placement="right"
        disableHoverListener={!siderCollapsed}
        arrow
      >
        <ListItemButton
          onClick={toggle}
          sx={{ pl: 2, justifyContent: "center" }}
        >
          <ListItemIcon
            sx={{
              justifyContent: "center",
              minWidth: "24px",
              transition: "margin-right 0.3s",
              marginRight: siderCollapsed ? "0px" : "12px",
              color: "currentColor",
            }}
          >
            {admin.icon}
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ noWrap: true, fontSize: "14px" }}
          />
          {open ? (
            <ExpandLess sx={{ color: "text.icon" }} />
          ) : (
            <ExpandMore sx={{ color: "text.icon" }} />
          )}
        </ListItemButton>
      </Tooltip>
      {!siderCollapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {admin.children.map((item, index) => {
              const group = item.meta?.adminGroup as AdminMenuGroup | undefined;
              const previous = admin.children[index - 1]?.meta?.adminGroup;
              const selected = item.key === selectedKey;
              const itemLabel = item.label ?? item.name;
              return (
                <Fragment key={item.key}>
                  {/* a section title where a new group starts */}
                  {group && group !== previous && (
                    <Box sx={{ pl: 3, pr: 2, pt: index === 0 ? 0.75 : 1.75 }}>
                      {index > 0 && <Divider sx={{ mb: 1.25, ml: 1 }} />}
                      <Typography
                        variant="caption"
                        component="div"
                        sx={{
                          pl: 1,
                          pb: 0.5,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.6,
                          textTransform: "uppercase",
                          color: "text.secondary",
                        }}
                      >
                        {translate(`menuGroups.${group}`)}
                      </Typography>
                    </Box>
                  )}
                  <ListItemButton
                    component={RouterLink}
                    to={item.route ?? ""}
                    selected={selected}
                    onClick={() => setMobileSiderOpen(false)}
                    sx={{
                      pl: 4,
                      py: 1.25,
                      justifyContent: "center",
                      color: selected ? "primary.main" : "text.primary",
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        justifyContent: "center",
                        transition: "margin-right 0.3s",
                        marginRight: "12px",
                        minWidth: "24px",
                        color: "currentColor",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={itemLabel}
                      primaryTypographyProps={{
                        noWrap: true,
                        fontSize: "14px",
                      }}
                    />
                  </ListItemButton>
                </Fragment>
              );
            })}
          </List>
        </Collapse>
      )}
    </div>
  );
};
